import { columnTypes } from '@/services/Grid/ColumnTypes';
import TooltipComponent from '@/components/Grid/Renderers/TooltipComponent';
import { cloneDeep, startCase, toLower } from 'lodash';
import ActionStatus from '@/services/Action/ActionStatus';
import ExternalLinkRenderer from '@/components/Grid/Renderers/ExternalLinkRenderer';
import store from '@/store';
import { isLastActivityDateEnabled, isReduceRiskRatingEnabled, isDateReferredToLegalEnabled } from '@/config/features';

function CapitilizeValue({ value }) {
  return startCase(toLower(value));
}

/* Riskhub Actions */
export const actionGridDefaultColDef = {
  filter: 'agSetColumnFilter',
  resizable: true,
  sortable: true,
  filterParams: {
    selectAllOnMiniFilter: false,
    // ag-Grid resets set filters on new rowData without this.
    newRowsAction: 'keep',
  },
};

export const actionGridColumnTypes = columnTypes;

const actionGridColumns = [
  {
    headerName: 'ID',
    enablePivot: true,
    field: 'clientActionId',
    colId: 'clientActionId',
    width: 130,
    minWidth: 130,
    filter: 'agSetColumnFilter',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    sort: 'asc',
    aggFunc: 'count',
    enableValue: true,
  },
  {
    headerName: 'Assessment Type',
    enablePivot: true,
    field: 'assessmentType',
    colId: 'assessmentType',
    width: 160,
    minWidth: 160,
    enableRowGroup: true,
    hide: true,
  },
  {
    headerName: ' Client Actions',
    enablePivot: true,
    field: 'numOfLinkedClientActions',
    colId: 'numOfLinkedClientActions',
    width: 150,
    minWidth: 150,
    enableRowGroup: true,
    cellStyle: { textAlign: 'center' },
    cellRendererFramework: ExternalLinkRenderer,
  },
  {
    headerName: 'FRA Action Due Date',
    enablePivot: true,
    field: 'dueDate',
    colId: 'dueDate',
    width: 150,
    minWidth: 150,
    type: 'dateColumn',
    enableRowGroup: true,
  },
  {
    headerName: 'Internal Due Date',
    enablePivot: true,
    field: 'internalDueDate',
    colId: 'internalDueDate',
    width: 200,
    minWidth: 200,
    type: 'dateColumn',
    enableRowGroup: true,
  },
  {
    headerName: 'Extended Due Date',
    enablePivot: true,
    field: 'extendedDueDate',
    colId: 'extendedDueDate',
    width: 200,
    minWidth: 200,
    type: 'dateColumn',
    enableRowGroup: true,
  },
  {
    headerName: 'Date Referred to Legal',
    enablePivot: true,
    field: 'dateReferredToLegal',
    colId: 'dateReferredToLegal',
    width: 200,
    minWidth: 200,
    type: 'dateColumn',
    enableRowGroup: true,
  },
  {
    headerName: 'Handover Date',
    enablePivot: true,
    field: 'handoverDate',
    colId: 'handoverDate',
    width: 150,
    minWidth: 150,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
    type: 'dateColumn',
  },
  {
    headerName: 'Work Plan',
    enablePivot: true,
    field: 'workPlanName',
    colId: 'workPlanName',
    width: 175,
    minWidth: 175,
    enableRowGroup: true,
  },
  {
    headerName: 'Work Plan Due Date',
    enablePivot: true,
    field: 'workPlanDueDate',
    colId: 'workPlanDueDate',
    width: 175,
    minWidth: 175,
    enableRowGroup: true,
    type: 'dateColumn',
  },
  {
    headerName: 'Tags',
    enablePivot: true,
    field: 'tags',
    colId: 'tags',
    width: 150,
    minWidth: 150,
    enableRowGroup: true,
  },
  {
    headerName: 'Global Status',
    enablePivot: true,
    field: 'status',
    colId: 'status',
    width: 175,
    minWidth: 175,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
    valueFormatter: params => CapitilizeValue(params),
    valueGetter: ({ data }) => {
      if (!data) { return ''; }

      const { status, secondaryStatus, completionStatus } = data;

      if (!secondaryStatus && !completionStatus) {
        return status;
      }

      const replacementStatus = completionStatus || secondaryStatus;
      const label = ActionStatus.getLabelForSecondaryOrCompletionStatus(replacementStatus);

      // If there is no label for it (such as 'full'), just return the normal status
      if (!label) {
        return status;
      }

      return label;
    },
    comparator: (valueA, valueB) => {
      if (valueA && valueB) {
        return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
      }

      return valueA ? 1 : -1;
    },
    filterParams: {
      cellRenderer: CapitilizeValue,
      // Column filterParams replace the default's outright, so repeat these.
      selectAllOnMiniFilter: false,
      newRowsAction: 'keep',
    },
  },
  {
    headerName: 'Review FRA Status',
    enablePivot: true,
    field: 'recommendationStatus',
    colId: 'recommendationStatus',
    width: 175,
    minWidth: 175,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Last Status Updated Date',
    enablePivot: true,
    field: 'lastStatusUpdatedDate',
    colId: 'lastStatusUpdatedDate',
    width: 225,
    minWidth: 225,
    type: 'dateColumn',
    enableRowGroup: true,
  },
  {
    headerName: 'User',
    enablePivot: true,
    field: 'reviewer',
    colId: 'reviewer',
    width: 200,
    minWidth: 200,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Team',
    enablePivot: true,
    field: 'currentResponsibleTeam',
    colId: 'currentResponsibleTeam',
    width: 225,
    minWidth: 225,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Last Submitted By',
    enablePivot: true,
    field: 'submittedBy',
    colId: 'submittedBy',
    width: 175,
    minWidth: 175,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Submitted for Approval By Team',
    enablePivot: true,
    field: 'submittedByTeam',
    colId: 'submittedByTeam',
    width: 250,
    minWidth: 250,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Submitted for Approval Date',
    enablePivot: true,
    field: 'submittedForApprovalDate',
    colId: 'submittedForApprovalDate',
    width: 250,
    minWidth: 250,
    type: 'dateColumn',
    enableRowGroup: true,
    hide: true,
  },
  {
    headerName: 'Action Type',
    enablePivot: true,
    field: 'actionType',
    colId: 'actionType',
    width: 220,
    minWidth: 220,
    filter: 'agSetColumnFilter',
    enableRowGroup: true,
  },
  {
    headerName: 'Risk Profile',
    enablePivot: true,
    enableRowGroup: true,
    field: 'riskProfile',
    colId: 'riskProfile',
    width: 200,
    minWidth: 200,
  },
  {
    headerName: 'Designation',
    enablePivot: true,
    enableRowGroup: true,
    field: 'designation',
    colId: 'designation',
    width: 240,
    minWidth: 240,
  },
  {
    headerName: 'UPRN',
    enablePivot: true,
    enableRowGroup: true,
    field: 'uprn',
    colId: 'uprn',
    width: 170,
    minWidth: 170,
  },
  {
    headerName: 'Parent UPRN',
    enablePivot: true,
    enableRowGroup: true,
    field: 'parentUprn',
    colId: 'parentUprn',
    width: 170,
    minWidth: 170,
  },
  {
    headerName: 'Hierarchy Level',
    enablePivot: true,
    enableRowGroup: true,
    field: 'propertyHierarchyType',
    colId: 'propertyHierarchyType',
    width: 170,
    minWidth: 170,
  },
  {
    headerName: 'Number Range',
    enablePivot: true,
    enableRowGroup: true,
    field: 'numberRange',
    colId: 'numberRange',
    width: 175,
    minWidth: 175,
  }, {
    headerName: 'Building Name',
    enablePivot: true,
    enableRowGroup: true,
    field: 'buildingName',
    colId: 'buildingName',
    width: 200,
    minWidth: 200,
  }, {
    headerName: 'Unit Number',
    enablePivot: true,
    enableRowGroup: true,
    field: 'unitNumber',
    colId: 'unitNumber',
    width: 120,
    minWidth: 120,
  }, {
    headerName: 'Street Number',
    enablePivot: true,
    enableRowGroup: true,
    field: 'streetNumber',
    colId: 'streetNumber',
    width: 150,
    minWidth: 150,
  }, {
    headerName: 'Street',
    enablePivot: true,
    enableRowGroup: true,
    field: 'street',
    colId: 'street',
    width: 150,
    minWidth: 150,
  }, {
    headerName: 'Town',
    enablePivot: true,
    enableRowGroup: true,
    field: 'town',
    colId: 'town',
    width: 150,
    minWidth: 150,
  }, {
    headerName: 'Postcode',
    enablePivot: true,
    enableRowGroup: true,
    field: 'postcode',
    colId: 'postcode',
    width: 125,
    minWidth: 125,
    filter: 'agTextColumnFilter',
  }, {
    headerName: 'Property Type',
    enablePivot: true,
    enableRowGroup: true,
    field: 'propertyType',
    colId: 'propertyType',
    width: 125,
    minWidth: 125,
  }, {
    headerName: 'Tenure',
    enablePivot: true,
    enableRowGroup: true,
    field: 'tenure',
    colId: 'tenure',
    width: 125,
    minWidth: 125,
    cellRendererFramework: TooltipComponent,
  }, {
    headerName: 'Hand-in RA Risk Rating',
    enablePivot: true,
    enableRowGroup: true,
    field: 'assessmentRiskRating',
    colId: 'assessmentRiskRating',
    width: 130,
    minWidth: 130,
    hide: true,
  }, {
    headerName: 'Potential RA Risk Rating',
    enablePivot: true,
    enableRowGroup: true,
    field: 'assessmentPotentialRiskRating',
    colId: 'assessmentPotentialRiskRating',
    width: 130,
    minWidth: 130,
    hide: true,
  }, {
    headerName: 'Current RA Risk Rating',
    enablePivot: true,
    enableRowGroup: true,
    field: 'assessmentCurrentRiskRating',
    colId: 'assessmentCurrentRiskRating',
    width: 130,
    minWidth: 130,
    hide: true,
  }, {
    headerName: 'Priority',
    enablePivot: true,
    enableRowGroup: true,
    field: 'priority',
    colId: 'priority',
    width: 130,
    minWidth: 130,
  }, {
    headerName: 'Question',
    enablePivot: true,
    enableRowGroup: true,
    field: 'question',
    colId: 'question',
    width: 150,
    minWidth: 150,
  }, {
    headerName: 'Location',
    enablePivot: true,
    enableRowGroup: true,
    field: 'location',
    colId: 'location',
    width: 150,
    minWidth: 150,
    hide: true,
  }, {
    headerName: 'Category',
    enablePivot: true,
    enableRowGroup: true,
    field: 'category',
    colId: 'category',
    width: 300,
    minWidth: 300,
  }, {
    headerName: 'Comments',
    enablePivot: true,
    enableRowGroup: true,
    field: 'comments',
    colId: 'comments',
    width: 250,
    minWidth: 250,
    cellRendererFramework: TooltipComponent,
    filter: 'agTextColumnFilter',
  }, {
    headerName: 'Notes',
    enablePivot: true,
    enableRowGroup: true,
    field: 'notes',
    colId: 'notes',
    width: 250,
    minWidth: 250,
    cellRendererFramework: TooltipComponent,
    filter: 'agTextColumnFilter',
  }, {
    headerName: 'Min Quantity',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'minQuantity',
    colId: 'minQuantity',
    width: 165,
    minWidth: 165,
  }, {
    headerName: 'Max Quantity',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'maxQuantity',
    colId: 'maxQuantity',
    width: 165,
    minWidth: 165,
  }, {
    headerName: 'Recommendation',
    enablePivot: true,
    enableRowGroup: true,
    field: 'recommendation',
    colId: 'recommendation',
    width: 400,
    minWidth: 400,
    cellRendererFramework: TooltipComponent,
    filter: 'agTextColumnFilter',
  }, {
    headerName: 'Specification',
    enablePivot: true,
    enableRowGroup: true,
    field: 'specification',
    colId: 'specification',
    width: 400,
    minWidth: 400,
    cellRendererFramework: TooltipComponent,
    filter: 'agTextColumnFilter',
  }, {
    headerName: 'Rate',
    enablePivot: true,
    enableRowGroup: true,
    enableValue: true,
    field: 'rate',
    colId: 'rate',
    width: 110,
    minWidth: 110,
  }, {
    headerName: 'Min Cost',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'minCost',
    colId: 'minCost',
    width: 140,
    minWidth: 140,
  }, {
    headerName: 'Max Cost',
    enablePivot: true,
    enableValue: true,
    enableRowGroup: true,
    field: 'maxCost',
    colId: 'maxCost',
    width: 140,
    minWidth: 140,
  }, {
    headerName: 'Last Activity Date',
    enablePivot: true,
    enableRowGroup: true,
    field: 'lastActivityDate',
    colId: 'lastActivityDate',
    width: 200,
    minWidth: 180,
    type: 'dateColumn',
    hide: true,
  }, {
    headerName: 'Last Activity',
    enablePivot: true,
    enableRowGroup: true,
    field: 'lastActivity',
    colId: 'lastActivity',
    width: 200,
    minWidth: 180,
    filter: 'agSetColumnFilter',
    hide: true,
  }, {
    headerName: 'Completed Date',
    enablePivot: true,
    enableRowGroup: true,
    field: 'completedDate',
    colId: 'completedDate',
    width: 170,
    minWidth: 170,
    type: 'dateColumn',
  }, {
    headerName: 'Completed By',
    enablePivot: true,
    enableRowGroup: true,
    field: 'completedBy',
    colId: 'completedBy',
    width: 150,
    minWidth: 150,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Building Height',
    enablePivot: true,
    enableRowGroup: true,
    field: 'buildingHeight',
    colId: 'buildingHeight',
    width: 110,
    minWidth: 110,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Building Height Source',
    enablePivot: true,
    enableRowGroup: true,
    field: 'buildingHeightSource',
    colId: 'buildingHeightSource',
    width: 140,
    minWidth: 140,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Current Evac Strategy',
    enablePivot: true,
    enableRowGroup: true,
    field: 'currentEvacStrategy',
    colId: 'currentEvacStrategy',
    width: 150,
    minWidth: 150,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Recommended Evac Strategy',
    enablePivot: true,
    enableRowGroup: true,
    field: 'recommendedEvacStrategy',
    colId: 'recommendedEvacStrategy',
    width: 150,
    minWidth: 150,
    filter: 'agSetColumnFilter',
  },
  {
    headerName: 'Work Order',
    enablePivot: true,
    enableRowGroup: true,
    field: 'workOrder',
    colId: 'workOrder',
    width: 150,
    minWidth: 150,
    filter: 'agSetColumnFilter',
  },
];

export const queryGridColumns = [
  {
    headerName: 'Query With',
    enablePivot: true,
    enableRowGroup: true,
    field: 'queryUser',
    colId: 'queryUser',
    width: 150,
    minWidth: 150,
    filter: 'agSetColumnFilter',
  },
];


export const getActionGridColumns = () => {
  let cols = cloneDeep(actionGridColumns);
  const user = store.getters && store.getters.authUser;
  // If feature flag disabled, drop the column; otherwise keep static definition/position
  // Each flag drops its column(s); applied cumulatively so all gates take effect.
  if (!isLastActivityDateEnabled(user)) {
    cols = cols.filter(c => c && c.field !== 'lastActivityDate');
  }
  if (!isReduceRiskRatingEnabled(user)) {
    cols = cols.filter(c => c && !['assessmentPotentialRiskRating', 'assessmentCurrentRiskRating'].includes(c.field));
  }
  if (!isDateReferredToLegalEnabled(user)) {
    cols = cols.filter(c => c && c.field !== 'dateReferredToLegal');
  }
  return cols;
};
